import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Icon } from "../icon/icon";
import chroma from "chroma-js";

const Wrapper = styled.div`
	position: relative;
	height: 28px;
	display: flex;
	align-items: center;

	cursor: pointer;
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;

	height: 28px;
	width: 100%;
	align-items: center;

	overflow: hidden;
	border-radius: 4px;
	&.opened {
		border-radius: 4px 4px 0px 0px;
	}

	background-color: ${props => props.theme.Gray1};
`;

const Label = styled.span`
	padding: 0px 12px;

	color: ${({ theme }) => theme.TextDefault};
	transition: color 300ms;

	font-family: "Roboto Flex", sans-serif;
	font-size: 14px;
	font-weight: 300;
	letter-spacing: 0.05em;
`;

const IconDiv = styled.div`
	min-width: 28px;
	width: 28px;
	max-width: 28px;

	min-height: 28px;
	height: 28px;
	max-height: 28px;

	//HACK TO align arrow to text font that has strange space above characters
	//position: relative;
	//bottom: -2.5px;
	display: flex;

	user-select: none;
	cursor: pointer;
	transition: transform 300ms ease-in;

	background-color: ${props => props.theme.Gray4};
`;

const Arrow = styled.img`
	position: relative;
	left: 5px;
	top: 5px;

	min-width: 18px;
	width: 18px;
	max-width: 18px;

	min-height: 18px;
	height: 18px;
	max-height: 18px;

	transition: transform 300ms ease-in;

	transform: rotate(270deg);
	&.closed {
		transform: rotate(90deg);
	}
`

const MenuWrapper = styled.div`
	position: absolute;
	top: 100%;
	right: 0;

	width: 100%;

	border-radius: 0px 0px 4px 4px;
	overflow: hidden;

	z-index: var(--z-level-2);
`;

const MenuEntry = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	height: 28px;

	//transition: background-color 200ms;
		//color: ${({ theme }) => theme.TextLight};

	//background-color: #FFF;
	//&:nth-child(odd) {
	//background-color: #EEE;
	//}

	background-color: ${({ theme }) => theme.Gray0};
	&:nth-child(even) {
		background-color: #F5F5F5;
	}

	&.selected {
		color: ${({ theme }) => theme.TextDark};
	}

	&:hover {
		background-color: ${props => chroma("#FFF").darken(0.7).css()};
	}
`;

// Ref: https://dev.to/rashed_iqbal/how-to-handle-outside-clicks-in-react-with-typescript-4lmc
// & https://www.robinwieruch.de/typescript-react-useref/
const useOutsideClick = (callback: Function) : React.MutableRefObject<HTMLDivElement | null> =>
{
	const ref = React.useRef<HTMLDivElement | null>(null);
	React.useEffect(() =>
	{
		const handleClick = (event: MouseEvent) =>
		{
			if (ref.current && !ref.current.contains(event.target as Node))
			{
				callback();
			}
		}

		document.addEventListener('click', handleClick);
		return () => document.removeEventListener('click', handleClick);
	}, [callback]);
	return ref;
};

export const ComboBox = ({
							 keyValue,
							 onChange,
							 displayOptions,
							 keyOptions,
							 enabled = true,
							 style, className
						 }: IProps) => {

	const { t } = useTranslation();
	const [showMenu, setShowMenu] = useState(false);
	const ref = useOutsideClick(() => setShowMenu(false));

	const handleOptionClick = (key: string) => {
		onChange?.(key);
		setShowMenu(false);
	};

	const Menu = useMemo(
		() => (
			<MenuWrapper>
				{keyOptions.map((key, index) => (
					<MenuEntry
						key={key}
						onClick={() => handleOptionClick(key)}
						className={keyValue === key ? "selected" : ""}
					>
						<Label>{t(displayOptions[index])}</Label>
					</MenuEntry>
				))}
			</MenuWrapper>
		),
		[displayOptions, keyOptions, keyValue]
	);

	return (
		<Wrapper
			ref={ref}
			onClick={() => {setShowMenu(!showMenu)}}
			className={`${enabled ? "" : "disabled"} ${className}`}
			style={style}
		>
			<Header className={showMenu ? "opened": ""}>
				<Label>{t(displayOptions[keyOptions.indexOf(keyValue)])}</Label>
				<IconDiv>
					<Arrow className={showMenu ? "" : "closed"} src={"/assets/rightArrow_light.svg"} width="18px"/>
				</IconDiv>
			</Header>
			{showMenu ? Menu : null}
		</Wrapper>
	);
};



interface IProps {
	keyValue: string,
	onChange: (keyValue: string) => void;
	displayOptions: string[], //will be translated
	keyOptions: string[],
	enabled?: boolean,
	style?: React.CSSProperties
	className?: string,
}
